import { CardColors, CardFooter, CardFooterItem } from "$components/card";
import { FormattedDate } from "$components/date/formatted-date";
import { makeStyles } from "$components/mui-styles";
import { MultiLineTypography } from "$components/multi-line-typography";
import {
  CardSubtitle,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import { Box, Grid, styled, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import type { FC } from "react";
import { FormattedMessage } from "react-intl";
import type { Disease } from "./disease-type";

const useStyles = makeStyles((theme) => ({
  observationContainer: {
    marginTop: theme.spacing(2),
  },
  observationDetail: {
    color: theme.palette.text.secondary,
  },
  diagnosisWrapper: {
    marginTop: theme.spacing(2),
  },
}));

const HeaderContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  flexGrow: 1,
}));

type FreeTextDiagnosis = {
  id: string;
  codification?: {
    name?: string;
  };
};

export const ConsultationHistoryCard: FC<{
  diagnoses: readonly FreeTextDiagnosis[];
  diseases?: readonly Disease[];
  problem?: string;
  presentingComplaint?: string;
  symptoms: Array<string>;
  plan?: string;
  deliveredOn: Date;
  deliveredBy?: string;
  locationName: string;
  consultationId: string;
}> = ({
  diagnoses,
  diseases = [],
  problem,
  presentingComplaint,
  symptoms = [],
  plan,
  deliveredOn,
  deliveredBy,
  locationName,
  consultationId,
}) => {
  const classes = useStyles();
  const hasExtraDetails =
    !!problem || !!plan || !!presentingComplaint || symptoms.length > 0;

  return (
    <PatientHistoryCard
      data-testid="patient.history.diagnosis.consultation"
      color={CardColors.CONSULTATION}
      expanded={hasExtraDetails}
      hideExpandIcon={true}
      HeaderComponent={() => (
        <HeaderContainer>
          <CardSubtitle color={CardColors.CONSULTATION}>
            <FormattedMessage
              id="patient.history.diagnosis.consultation.title"
              defaultMessage="Consultation"
            />
          </CardSubtitle>
          <Box flexDirection="column">
            {diseases.map((disease) => (
              <Typography key={disease.uuid} variant="h5">
                <strong>{disease.displayName}</strong>
              </Typography>
            ))}
            {diagnoses.map((diagnosis) => (
              <Typography key={diagnosis.id} variant="h5">
                <strong>{diagnosis.codification?.name ?? ""}</strong>
              </Typography>
            ))}
          </Box>
        </HeaderContainer>
      )}
      FooterComponent={() => (
        <CardFooter>
          <CardFooterItem
            label={
              <FormattedMessage
                id="patient.history.diagnosis.deliveredOn"
                defaultMessage="Delivered:"
              />
            }
          >
            <FormattedDate value={deliveredOn} />
          </CardFooterItem>
          <CardFooterItem
            label={
              <FormattedMessage
                id="patient.history.diagnosis.deliveredBy"
                defaultMessage="By:"
              />
            }
          >
            {deliveredBy ? (
              deliveredBy
            ) : (
              <Skeleton style={{ display: "inline-block", width: "100px" }} />
            )}
          </CardFooterItem>
          {locationName && (
            <CardFooterItem
              label={
                <FormattedMessage
                  id="patient.history.diagnosis.location"
                  defaultMessage="Location:"
                />
              }
            >
              {locationName}
            </CardFooterItem>
          )}
        </CardFooter>
      )}
    >
      {hasExtraDetails && (
        <Box mb={1}>
          <Grid container className={classes.observationContainer}>
            {problem && (
              <Grid item xs={12}>
                <Typography
                  variant="body2"
                  className={classes.observationDetail}
                >
                  <FormattedMessage
                    id="patient.history.diagnosis.problem"
                    defaultMessage="Problem"
                  />
                </Typography>
                <MultiLineTypography variant="body2">
                  {problem}
                </MultiLineTypography>
              </Grid>
            )}
            {(presentingComplaint || symptoms.length > 0) && (
              <Grid item xs={12} className={classes.diagnosisWrapper}>
                <Typography
                  variant="body2"
                  className={classes.observationDetail}
                >
                  <FormattedMessage
                    id="patient.history.diagnosis.presentingComplaint"
                    defaultMessage="Presenting complaint"
                  />
                </Typography>
                <MultiLineTypography variant="body2">
                  {presentingComplaint}
                </MultiLineTypography>
                {symptoms.map((symptom) => (
                  <Typography key={symptom} variant="body2">
                    {symptom}
                  </Typography>
                ))}
              </Grid>
            )}
            {plan && (
              <Grid item xs={12} className={classes.diagnosisWrapper}>
                <Typography
                  variant="body2"
                  className={classes.observationDetail}
                >
                  <FormattedMessage
                    id="patient.history.diagnosis.plan"
                    defaultMessage="Plan"
                  />
                </Typography>
                <MultiLineTypography variant="body2">
                  {plan}
                </MultiLineTypography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}
    </PatientHistoryCard>
  );
};
